import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";
import { colToIndex, indexToCol, parseCellRef, shiftFormulaForColDelete, shiftFormulaForColInsert, shiftFormulaForRowDelete, shiftFormulaForRowInsert, } from "../../lib/spreadsheet";
import { SpreadsheetGrid } from "./Grid";
export function SpreadsheetModal({ node, preview: initialPreview, valuationDate, loadPreviewInputs, onClose, onSave, }) {
    const initialCells = node.config.cells ?? {};
    const [cells, setCells] = useState(initialCells);
    const [namedRanges, setNamedRanges] = useState(node.config.named_ranges ?? []);
    const [outputs, setOutputs] = useState(node.config.outputs ?? []);
    const [styles, setStyles] = useState(node.config.styles ?? []);
    const [livePreview, setLivePreview] = useState(initialPreview);
    // Manually-typed test values — these PERSIST across editor sessions.
    // They are NEVER used at workflow run time.
    const [testInputs, setTestInputs] = useState(node.config.test_inputs ?? {});
    // Auto-resolved upstream values — ephemeral, recomputed on open.
    const [autoInputs, setAutoInputs] = useState({});
    const [skippedInputs, setSkippedInputs] = useState([]);
    // The inputs / named ranges / outputs / styles config panels eat a
    // lot of vertical space when several are present; default to
    // collapsed so the grid itself dominates the modal.
    const [configOpen, setConfigOpen] = useState(false);
    // On open: if the editor knows how to resolve upstream values, fetch
    // them once. The result populates the test-inputs panel as suggestions
    // (placeholders); user-typed values still take precedence.
    useEffect(() => {
        if (!loadPreviewInputs)
            return;
        let cancelled = false;
        (async () => {
            try {
                const r = await loadPreviewInputs(node.id);
                if (cancelled)
                    return;
                setAutoInputs(r.resolved ?? {});
                setSkippedInputs(r.skipped ?? []);
            }
            catch {
                // Non-fatal — the user can still type values manually.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadPreviewInputs, node.id]);
    // Live preview as the user edits. Inputs are layered:
    //   auto-resolved (ephemeral, from upstream)  ← bottom
    //   user test inputs (persisted)              ← top
    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const inputs = { ...autoInputs };
                // Editor convenience: the approach version's date stands in for
                // the valuation date. INPUT("valuation_date") and the engine's
                // VALUATION_DATE() function resolve to the same string so date
                // formulas always have an anchor at edit time. At runtime the
                // real valuation's date is wired in via the upstream bus.
                if (valuationDate && inputs.valuation_date == null) {
                    inputs.valuation_date = valuationDate;
                }
                for (const [k, v] of Object.entries(testInputs)) {
                    if (v === "")
                        continue;
                    const n = Number(v);
                    inputs[k] = isNaN(n) ? v : n;
                }
                const r = await api.post("/workflow/spreadsheet/preview", {
                    cells,
                    inputs,
                    named_ranges: namedRanges,
                    outputs,
                    styles,
                    valuation_date: valuationDate ?? null,
                });
                if (!cancelled)
                    setLivePreview(r);
            }
            catch {
                if (!cancelled)
                    setLivePreview(null);
            }
        }, 120);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [cells, testInputs, autoInputs, valuationDate, namedRanges, outputs, styles]);
    // Close on Escape so users always have an escape hatch.
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape")
                onClose();
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    // While the modal is mounted, swallow Delete/Backspace at document level
    // (capture phase) so React Flow's keyboard handler can't delete the
    // surrounding spreadsheet node when the user is just clearing a cell.
    // stopPropagation in the capture phase also blocks the grid div's own
    // keydown — so we invoke the grid's clearRange via this ref before
    // swallowing.
    const clearRangeRef = useRef(null);
    useEffect(() => {
        function block(e) {
            if (e.key !== "Delete" && e.key !== "Backspace")
                return;
            const t = e.target;
            // Let inputs handle their own deletes — only block when focus is on
            // a non-input element (e.g. the grid div).
            const tag = t?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA")
                return;
            e.stopPropagation();
            e.preventDefault();
            clearRangeRef.current?.();
        }
        document.addEventListener("keydown", block, true);
        return () => document.removeEventListener("keydown", block, true);
    }, []);
    /** Insert/delete row or column. Atomically rewrites cells (re-keys
     * IDs past the boundary AND shifts every formula's references),
     * outputs (cell ref shifted), and named_ranges (ref shifted using
     * the same range/cell parser). Anchored components (`$row` / `$col`)
     * stay put; deleted refs become `#REF!`. */
    function applyStructuralOp(op, pos) {
        // Decide ref-shift function for THIS particular op. Only run on
        // FORMULAS (cells starting with `=`). Literal text and number
        // values pass through unchanged — otherwise a label like
        // "Note B12 below" would have its `B12` rewritten as if it were
        // a cell reference.
        const shiftFormula = (cellText) => {
            if (typeof cellText !== "string" || !cellText.startsWith("=")) {
                return cellText;
            }
            if (op === "insertRow")
                return shiftFormulaForRowInsert(cellText, pos, 1);
            if (op === "deleteRow")
                return shiftFormulaForRowDelete(cellText, pos, 1);
            if (op === "insertCol")
                return shiftFormulaForColInsert(cellText, pos, 1);
            return shiftFormulaForColDelete(cellText, pos, 1);
        };
        // Re-key cells by transforming their ID (the row/col coordinate)
        // AND by rewriting their formula text.
        const nextCells = {};
        for (const [ref, formula] of Object.entries(cells)) {
            const p = parseCellRef(ref);
            if (!p) {
                nextCells[ref] = formula;
                continue;
            }
            let newCol = p.col;
            let newRow = p.row;
            let drop = false;
            if (op === "insertRow") {
                if (p.row >= pos)
                    newRow = p.row + 1;
            }
            else if (op === "deleteRow") {
                if (p.row === pos)
                    drop = true;
                else if (p.row > pos)
                    newRow = p.row - 1;
            }
            else if (op === "insertCol") {
                const ci = colToIndex(p.col);
                if (ci >= pos)
                    newCol = indexToCol(ci + 1);
            }
            else { // deleteCol
                const ci = colToIndex(p.col);
                if (ci === pos)
                    drop = true;
                else if (ci > pos)
                    newCol = indexToCol(ci - 1);
            }
            if (drop)
                continue;
            nextCells[`${newCol}${newRow}`] = shiftFormula(formula);
        }
        setCells(nextCells);
        // Outputs: shift the `cell` ref. Outputs pointing at a deleted
        // row/col become "" so the user notices in the editor list.
        const nextOutputs = outputs.map((o) => {
            if (!o.cell)
                return o;
            const p = parseCellRef(o.cell);
            if (!p)
                return o;
            let newCol = p.col;
            let newRow = p.row;
            if (op === "insertRow" && p.row >= pos)
                newRow = p.row + 1;
            else if (op === "deleteRow") {
                if (p.row === pos)
                    return { ...o, cell: "" };
                if (p.row > pos)
                    newRow = p.row - 1;
            }
            else if (op === "insertCol") {
                const ci = colToIndex(p.col);
                if (ci >= pos)
                    newCol = indexToCol(ci + 1);
            }
            else if (op === "deleteCol") {
                const ci = colToIndex(p.col);
                if (ci === pos)
                    return { ...o, cell: "" };
                if (ci > pos)
                    newCol = indexToCol(ci - 1);
            }
            return { ...o, cell: `${newCol}${newRow}` };
        });
        setOutputs(nextOutputs);
        // Named ranges: each `ref` is either a cell or "A1:B5". Shift
        // each endpoint independently. Deleted endpoints → null → drop.
        const shiftEndpoint = (end) => {
            let newCol = end.col;
            let newRow = end.row;
            if (op === "insertRow") {
                if (!end.rowAbs && end.row >= pos)
                    newRow = end.row + 1;
            }
            else if (op === "deleteRow") {
                if (end.row === pos)
                    return null;
                if (!end.rowAbs && end.row > pos)
                    newRow = end.row - 1;
            }
            else if (op === "insertCol") {
                const ci = colToIndex(end.col);
                if (!end.colAbs && ci >= pos)
                    newCol = indexToCol(ci + 1);
            }
            else if (op === "deleteCol") {
                const ci = colToIndex(end.col);
                if (ci === pos)
                    return null;
                if (!end.colAbs && ci > pos)
                    newCol = indexToCol(ci - 1);
            }
            const colP = end.colAbs ? "$" : "";
            const rowP = end.rowAbs ? "$" : "";
            return `${colP}${newCol}${rowP}${newRow}`;
        };
        const nextRanges = namedRanges
            .map((nr) => {
            if (!nr.ref)
                return nr;
            const parts = nr.ref.split(":");
            if (parts.length === 1) {
                const p = parseCellRef(parts[0]);
                if (!p)
                    return nr;
                const e = shiftEndpoint(p);
                return e === null ? null : { ...nr, ref: e };
            }
            const p1 = parseCellRef(parts[0]);
            const p2 = parseCellRef(parts[1]);
            if (!p1 || !p2)
                return nr;
            const e1 = shiftEndpoint(p1);
            const e2 = shiftEndpoint(p2);
            if (!e1 || !e2)
                return null;
            return { ...nr, ref: `${e1}:${e2}` };
        })
            .filter((x) => x !== null);
        setNamedRanges(nextRanges);
    }
    // Render through a portal so we're not constrained by any ancestor's
    // overflow/transform/filter — fixed positioning can otherwise be reparented
    // unexpectedly.
    return createPortal(_jsx("div", { className: "fixed inset-0 z-[100] flex items-stretch bg-ink-900/80", children: _jsxs("div", { className: "m-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-ink-700 bg-ink-800 shadow-2xl", children: [_jsxs("header", { className: "flex items-center justify-between border-b border-ink-700 px-5 py-3", children: [_jsxs("div", { children: [_jsxs("div", { className: "text-[10px] font-mono uppercase tracking-wider text-ink-500", children: ["spreadsheet \u00B7 ", node.id] }), _jsx("h2", { className: "text-sm font-medium text-ink-50", children: "Workflow spreadsheet" }), _jsxs("p", { className: "text-xs text-ink-400", children: [_jsx("code", { className: "font-mono", children: "INPUT(\"name\")" }), " creates an input port.", " ", _jsx("code", { className: "font-mono", children: "OUTPUT(\"name\", value)" }), " creates an output port."] })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { className: "btn-ghost text-xs", onClick: onClose, children: "Cancel" }), _jsx("button", { className: "btn-primary text-xs", onClick: () => onSave({
                                        cells,
                                        named_ranges: namedRanges,
                                        outputs,
                                        styles,
                                        test_inputs: testInputs,
                                    }), children: "Apply" })] })] }), livePreview?.warnings && livePreview.warnings.length > 0 && (_jsxs("div", { className: "border-b border-amber-700/50 bg-amber-900/30 px-5 py-2 text-xs text-amber-200", children: [_jsx("div", { className: "mb-1 font-semibold", children: "Configuration warnings" }), _jsx("ul", { className: "list-disc pl-5 space-y-0.5", children: livePreview.warnings.map((w, i) => (_jsx("li", { children: w }, i))) })] })), _jsx("div", { className: "border-b border-ink-700 bg-ink-900/60", children: _jsxs("button", { type: "button", onClick: () => setConfigOpen((o) => !o), className: "flex w-full items-center gap-2 px-5 py-1.5 text-left text-xs text-ink-300 hover:bg-ink-800/40", children: [_jsx("span", { className: "inline-block w-3 font-mono text-ink-500", children: configOpen ? "▾" : "▸" }), _jsx("span", { className: "font-medium text-ink-200", children: "Configuration" }), _jsxs("span", { className: "text-ink-500", children: [livePreview?.input_ports.length ?? 0, " input", (livePreview?.input_ports.length ?? 0) === 1 ? "" : "s", " · ", namedRanges.length, " named range", namedRanges.length === 1 ? "" : "s", " · ", outputs.length, " output", outputs.length === 1 ? "" : "s", " · ", styles.length, " style", styles.length === 1 ? "" : "s"] })] }) }), configOpen && livePreview && livePreview.input_ports.length > 0 && (_jsxs("div", { className: "border-b border-ink-700 bg-ink-900/40 px-5 py-2 text-xs", children: [_jsxs("div", { className: "mb-1 flex items-baseline gap-2", children: [_jsx("span", { className: "text-ink-500", children: "Test inputs" }), _jsx("span", { className: "text-[11px] text-ink-600", children: "\u2014 auto-loaded from upstream where possible; manual values persist but are NEVER used at runtime." })] }), _jsx("div", { className: "overflow-y-auto", style: { maxHeight: "9rem" }, children: _jsx("span", { className: "inline-flex flex-wrap items-center gap-x-3 gap-y-1", children: livePreview.input_ports.map((p) => {
                                    const auto = autoInputs[p];
                                    const skip = skippedInputs.find((s) => s.port === p);
                                    const hasAuto = auto !== undefined && auto !== null;
                                    const hasTest = (testInputs[p] ?? "") !== "";
                                    const unresolved = !hasAuto && !hasTest && !skip;
                                    const colour = unresolved
                                        ? "text-red-300 [[data-theme=light]_&]:text-red-700"
                                        : skip
                                            ? "text-amber-300 [[data-theme=light]_&]:text-amber-700"
                                            : "text-emerald-300 [[data-theme=light]_&]:text-emerald-700";
                                    const tooltip = unresolved
                                        ? "no upstream value — provide a test value or wire the upstream"
                                        : skip?.reason;
                                    return (_jsxs("label", { className: "inline-flex items-center gap-1.5", children: [_jsx("span", { className: `font-mono ${colour}`, title: tooltip ?? undefined, children: p }), _jsx("span", { className: "text-ink-600", children: "=" }), _jsx("input", { className: "w-32 rounded border border-ink-700 bg-ink-900 px-2 py-0.5 font-mono text-xs text-ink-100 focus:border-accent-500 focus:outline-none", placeholder: auto !== undefined && auto !== null
                                                    ? typeof auto === "string"
                                                        ? auto
                                                        : JSON.stringify(auto)
                                                    : skip
                                                        ? "(needs manual value)"
                                                        : "(unset)", value: testInputs[p] ?? "", onChange: (e) => setTestInputs({ ...testInputs, [p]: e.target.value }) })] }, p));
                                }) }) }), skippedInputs.length > 0 && (_jsxs("p", { className: "mt-1 text-[11px] text-amber-400", children: [skippedInputs.length, " port(s) couldn't be auto-loaded \u2014", " ", skippedInputs.map((s) => s.port).join(", "), ". They depend on something not loadable at edit time (typically an AI agent upstream)."] }))] })), configOpen && (_jsx(NamedRangesAndOutputs, { namedRanges: namedRanges, outputs: outputs, onChangeNamedRanges: setNamedRanges, onChangeOutputs: setOutputs })), configOpen && _jsx(StylesPanel, { styles: styles, onChange: setStyles }), _jsx("div", { className: "min-h-0 flex-1 overflow-hidden", children: _jsx(SpreadsheetGrid, { cells: cells, preview: livePreview, onChange: setCells, onInsertRow: (before) => applyStructuralOp("insertRow", before), onDeleteRow: (at) => applyStructuralOp("deleteRow", at), onInsertCol: (beforeIdx) => applyStructuralOp("insertCol", beforeIdx), onDeleteCol: (atIdx) => applyStructuralOp("deleteCol", atIdx), clearRangeRef: clearRangeRef }) })] }) }), document.body);
}
function NamedRangesAndOutputs({ namedRanges, outputs, onChangeNamedRanges, onChangeOutputs, }) {
    // Collapse each panel by default once it has entries — otherwise a
    // big bond model with 30+ named ranges pushes the grid below the
    // fold. Keep them open when empty so the explainer copy is visible.
    const [showNames, setShowNames] = useState(namedRanges.length === 0);
    const [showOutputs, setShowOutputs] = useState(outputs.length === 0);
    return (_jsxs("div", { className: "grid grid-cols-2 gap-3 border-b border-ink-700 bg-ink-900/40 px-5 py-2 text-xs", children: [_jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("button", { type: "button", className: "flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-400 hover:text-ink-200", onClick: () => setShowNames((v) => !v), children: [_jsx("span", { children: showNames ? "▾" : "▸" }), _jsx("span", { children: "Named ranges" }), namedRanges.length > 0 && (_jsxs("span", { className: "text-ink-500", children: ["(", namedRanges.length, ")"] }))] }), showNames && (_jsx("button", { className: "btn-ghost text-[11px]", onClick: () => onChangeNamedRanges([...namedRanges, { name: "", ref: "" }]), children: "+ Add" }))] }), showNames && namedRanges.length === 0 && (_jsxs("p", { className: "mt-1 text-[11px] text-ink-500", children: ["None. Naming a cell or range lets formulas use the bare name \u2014 e.g. after ", _jsx("code", { className: "font-mono", children: "principal \u2192 A1" }), ",", " ", _jsx("code", { className: "font-mono", children: "=principal*0.05" }), " works and survives moving A1 (just update the named range)."] })), showNames && namedRanges.length > 0 && (_jsx("ul", { className: "mt-1 space-y-1 overflow-y-auto", style: { maxHeight: "12rem" }, children: namedRanges.map((nr, i) => (_jsxs("li", { className: "flex items-center gap-1", children: [_jsx("input", { className: "input flex-1 font-mono text-xs", value: nr.name, placeholder: "name", onChange: (e) => onChangeNamedRanges(namedRanges.map((n, j) => (j === i ? { ...n, name: e.target.value } : n))) }), _jsx("span", { className: "text-ink-500", children: "\u2190" }), _jsx("input", { className: "input w-28 font-mono text-xs", value: nr.ref, placeholder: "A1 or A1:B5", onChange: (e) => onChangeNamedRanges(namedRanges.map((n, j) => (j === i ? { ...n, ref: e.target.value } : n))) }), _jsx("button", { className: "btn-ghost text-[11px] text-red-300", onClick: () => onChangeNamedRanges(namedRanges.filter((_, j) => j !== i)), children: "\u00D7" })] }, i))) }))] }), _jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("button", { type: "button", className: "flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-400 hover:text-ink-200", onClick: () => setShowOutputs((v) => !v), children: [_jsx("span", { children: showOutputs ? "▾" : "▸" }), _jsx("span", { children: "Outputs" }), outputs.length > 0 && (_jsxs("span", { className: "text-ink-500", children: ["(", outputs.length, ")"] }))] }), showOutputs && (_jsx("button", { className: "btn-ghost text-[11px]", onClick: () => onChangeOutputs([...outputs, { port: "", cell: "" }]), children: "+ Add" }))] }), showOutputs && outputs.length === 0 && (_jsxs("p", { className: "mt-1 text-[11px] text-ink-500", children: ["Declare each output port as ", _jsx("em", { children: "port \u2192 cell" }), ". E.g.", " ", _jsx("code", { className: "font-mono", children: "present_value \u2192 B12" }), ". The workflow node will expose a port for each row here."] })), showOutputs && outputs.length > 0 && (_jsx("ul", { className: "mt-1 space-y-1 overflow-y-auto", style: { maxHeight: "12rem" }, children: outputs.map((o, i) => (_jsxs("li", { className: "flex items-center gap-1", children: [_jsx("input", { className: "input flex-1 font-mono text-xs", value: o.port, placeholder: "port name", onChange: (e) => onChangeOutputs(outputs.map((x, j) => (j === i ? { ...x, port: e.target.value } : x))) }), _jsx("span", { className: "text-ink-500", children: "\u2190" }), _jsx("input", { className: "input w-20 font-mono text-xs", value: o.cell, placeholder: "A1", onChange: (e) => onChangeOutputs(outputs.map((x, j) => j === i ? { ...x, cell: e.target.value.toUpperCase() } : x)) }), _jsx("button", { className: "btn-ghost text-[11px] text-red-300", onClick: () => onChangeOutputs(outputs.filter((_, j) => j !== i)), children: "\u00D7" })] }, i))) }))] })] }));
}
const FG_OPTIONS = [
    undefined, "default", "muted", "accent", "success", "warning", "danger",
];
const BG_OPTIONS = [
    undefined, "none", "header", "subtotal", "total", "input", "output", "note",
];
const ALIGN_OPTIONS = [
    undefined, "left", "center", "right",
];
function StylesPanel({ styles, onChange, }) {
    const [show, setShow] = useState(styles.length === 0);
    function setAt(i, patch) {
        onChange(styles.map((s, j) => (j === i ? { ...s, ...patch } : s)));
    }
    return (_jsxs("div", { className: "border-b border-ink-700 bg-ink-900/40 px-5 py-2 text-xs", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("button", { type: "button", className: "flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-400 hover:text-ink-200", onClick: () => setShow((v) => !v), children: [_jsx("span", { children: show ? "▾" : "▸" }), _jsx("span", { children: "Styles" }), styles.length > 0 && (_jsxs("span", { className: "text-ink-500", children: ["(", styles.length, ")"] }))] }), show && (_jsx("button", { className: "btn-ghost text-[11px]", onClick: () => onChange([...styles, { ref: "" }]), children: "+ Add" }))] }), show && styles.length === 0 && (_jsxs("p", { className: "mt-1 text-[11px] text-ink-500", children: ["None. Style a cell or range \u2014 bold totals, header rows, currency formats. Purely cosmetic; engine ignores. The Equity DCF / model builder agent sets these via ", _jsx("code", { className: "font-mono", children: "upsert_style" }), "."] })), show && styles.length > 0 && (_jsx("ul", { className: "mt-1 space-y-1 overflow-y-auto", style: { maxHeight: "14rem" }, children: styles.map((s, i) => (_jsxs("li", { className: "flex flex-wrap items-center gap-1", children: [_jsx("input", { className: "input w-24 font-mono text-xs", value: s.ref, placeholder: "A1 or A1:F1", onChange: (e) => setAt(i, { ref: e.target.value.toUpperCase() }) }), _jsxs("label", { className: "flex items-center gap-1 text-[11px] text-ink-300", children: [_jsx("input", { type: "checkbox", checked: !!s.bold, onChange: (e) => setAt(i, { bold: e.target.checked }) }), "B"] }), _jsxs("label", { className: "flex items-center gap-1 text-[11px] italic text-ink-300", children: [_jsx("input", { type: "checkbox", checked: !!s.italic, onChange: (e) => setAt(i, { italic: e.target.checked }) }), "I"] }), _jsx("select", { className: "input w-20 text-xs", value: s.align ?? "", onChange: (e) => setAt(i, { align: (e.target.value || undefined) }), children: ALIGN_OPTIONS.map((a) => (_jsx("option", { value: a ?? "", children: a ?? "align" }, a ?? ""))) }), _jsx("select", { className: "input w-24 text-xs", value: s.fg ?? "", onChange: (e) => setAt(i, { fg: (e.target.value || undefined) }), children: FG_OPTIONS.map((v) => (_jsx("option", { value: v ?? "", children: v ? `fg:${v}` : "fg" }, v ?? ""))) }), _jsx("select", { className: "input w-28 text-xs", value: s.bg ?? "", onChange: (e) => setAt(i, { bg: (e.target.value || undefined) }), children: BG_OPTIONS.map((v) => (_jsx("option", { value: v ?? "", children: v ? `bg:${v}` : "bg" }, v ?? ""))) }), _jsx("input", { className: "input w-28 font-mono text-xs", value: s.num_format ?? "", placeholder: "0.00 / 0.00% / $#,##0", onChange: (e) => setAt(i, { num_format: e.target.value || undefined }) }), _jsx("button", { className: "btn-ghost text-[11px] text-red-300", onClick: () => onChange(styles.filter((_, j) => j !== i)), children: "\u00D7" })] }, i))) }))] }));
}
